import { withDurationMetric } from "@/src/lib/metrics";
import { customCamelCase, customSnakeCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { Database } from "@/src/lib/types/database.types";
import { PayerRow } from "@/src/lib/types/tables.types";
import { Payer } from "@/src/models/payer";

export const PAYER_TABLE_NAME = "payer";
export const fullPayerSelect = `
  *
`;

export function mapPayerRowToPayer(payerRow: any): Payer {
  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(payerRow)) {
    mappedFields[customCamelCase(key)] = value;
  }

  return {
    ...mappedFields,
  } as Payer;
}

function mapPayerToPayerRow(payer: Payer): PayerRow {
  const { ...rest } = payer;
  // const cleanedPayer = enforceShape(rest, payerShape);

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(rest)) {
    mappedFields[customSnakeCase(key)] = value;
  }

  return {
    ...mappedFields,
  } as PayerRow;
}

/**
 * Get a payer
 * @param payerId - The id of the payer
 * @returns The payer
 */
export const getPayer = withDurationMetric(async function getPayer(payerId: string): Promise<Payer | null> {
  const supabase = await getSupabaseClient();

  const { data: payerRow, error } = await supabase.from(PAYER_TABLE_NAME).select(fullPayerSelect).eq("id", payerId);

  if (error) {
    console.error("Failed to fetch payer:", error);

    return null;
  }

  return payerRow ? mapPayerRowToPayer(payerRow) : null;
}, "getPayer");

/**
 * Get all payers
 * @returns The payers
 */
export const getAllPayers = withDurationMetric(async function getAllPayers(): Promise<Payer[]> {
  const supabase = await getSupabaseClient();

  try {
    const { data, error } = await supabase.from("payer").select("*").is("deleted_at", null).order("name");

    if (error) {
      console.error("Failed to fetch payer:", error);

      return [];
    }

    const payersToReturn = data?.map((payer) => mapPayerRowToPayer(payer));

    return payersToReturn;
  } catch (error) {
    console.error("Failed to fetch all payer:", error);

    return [];
  }
}, "getAllPayers");

/**
 * Get a payer by coverage key: legacy short_id or primary_payer_id (Stedi).
 */
export const getPayerByCustomerPayerKey = withDurationMetric(async function getPayerByCustomerPayerKey(
  key: string
): Promise<Payer | null> {
  const supabase = await getSupabaseClient();

  const { data: byShort, error: shortError } = await supabase
    .from("payer")
    .select(fullPayerSelect)
    .eq("short_id", key)
    .is("deleted_at", null)
    .maybeSingle();

  if (shortError) {
    console.error("Failed to fetch payer by short_id:", shortError);

    return null;
  }

  if (byShort) {
    return mapPayerRowToPayer(byShort);
  }

  const { data: byPrimary, error: primaryError } = await supabase
    .from("payer")
    .select(fullPayerSelect)
    .eq("primary_payer_id", key)
    .is("deleted_at", null)
    .maybeSingle();

  if (primaryError) {
    console.error("Failed to fetch payer by primary_payer_id:", primaryError);

    return null;
  }

  return byPrimary ? mapPayerRowToPayer(byPrimary) : null;
}, "getPayerByCustomerPayerKey");

/** @deprecated Prefer getPayerByCustomerPayerKey — key may be short_id or primary_payer_id */
export const getPayerByShortId = getPayerByCustomerPayerKey;

/**
 * Insert a payer
 * @param payer - The payer to insert
 * @returns The inserted payer
 */
export const insertPayer = withDurationMetric(async function insertPayer(payer: Payer): Promise<Payer[]> {
  const supabase = await getSupabaseClient();

  const { data, error } = await supabase.from("payer").insert(mapPayerToPayerRow(payer)).select(fullPayerSelect);

  if (error) {
    console.error("Failed to insert payer:", error);

    return [];
  }

  const payerToReturn = data ? data.map((payer) => mapPayerRowToPayer(payer)) : [];

  return payerToReturn;
}, "insertPayer");

/**
 * Create or update a payer
 * @param payer - The payer to create or update
 * @returns The created or updated payer
 */
export const createOrUpdatePayer = withDurationMetric(async function createOrUpdatePayer(
  payer: Payer
): Promise<Payer | null> {
  const supabase = await getSupabaseClient();

  const { data, error } = await supabase.from("payer").upsert(mapPayerToPayerRow(payer)).select(fullPayerSelect);

  if (error) {
    console.error("Failed to create or update payer:", error);

    return payer;
  }

  return data ? mapPayerRowToPayer(data) : null;
}, "createOrUpdatePayer");

/**
 * Update a payer
 * @param payerId - The id of the payer
 * @param payer - The payer to update
 * @returns The updated payer
 */
export const updatePayer = withDurationMetric(async function updatePayer(
  payerId: string,
  payer: Partial<Payer>
): Promise<Payer | null> {
  const supabase = await getSupabaseClient();

  const updateRow = {
    ...Object.fromEntries(Object.entries(payer).map(([key, value]) => [customSnakeCase(key), value])),
    updated_at: new Date().toISOString(),
  } as Database["public"]["Tables"]["payer"]["Update"];

  const { data, error } = await supabase
    .from("payer")
    .update(updateRow)
    .eq("id", payerId)
    .select(fullPayerSelect)
    .single();

  if (error) {
    console.error("Failed to update payer:", error);

    return null;
  }

  return data ? mapPayerRowToPayer(data) : null;
}, "updatePayer");
